import moment from 'moment'
import { OrderProperties } from '@/infrastructure/network/Monitaro/schema/models'
import PaymentEntity, { IPaymentProps } from '@/entities/Payment'
import UserEntity from '@/entities/User'

export interface IOrderProps {
  id?: string
  payments?: IPaymentProps[]
  userId: string | null
  title: string | null
  payment: string | null
  status: string | null
  amount: number | null
  subtotal: number | null
  commission: number | null
  tax: number | null
  gmoOrderNo: string | null
  gmoAccessNo: string | null
  gmoAccessPass: string | null
  gmoBranchNo: number | null
  gmoCardNo: number | null
  gmoConfNo: string | null
  gmoReceiptNo: string | null
  gmoReceiptUrl: string | null
  gmoTranNo: string | null
  gmoTranDate: string | null
  gmoConvenienceNo: string | null
  confirmed: boolean
  startedAt: string | null
  endedAt: string | null
  sentAt: string | null
  createdAt: string | null
  updatedAt: string | null
  properties: OrderProperties | null
  user: UserEntity | null
}

export default class OrderEntity {
  private _props: IOrderProps

  constructor(props?: IOrderProps | null) {
    this._props = EmptyOrderPropsFactory(props)
  }

  get props(): IOrderProps {
    return this._props
  }

  get clone(): IOrderProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get payments(): PaymentEntity[] | undefined {
    return this._props.payments ? this._props.payments.map((prop) => new PaymentEntity(prop)) : []
  }

  get userId(): string | null {
    return this._props.userId
  }

  get title(): string | null {
    return this._props.title
  }

  get range(): string | null {
    return moment(this._props.startedAt).format('MM/DD') + ' - ' + moment(this._props.endedAt).format('MM/DD')
  }

  get payment(): string | null {
    const payments = [
      { text: 'クレジットカード', value: 'credit' },
      { text: 'コンビニ', value: 'convenience' }
    ]
    return payments.find((prop) => prop.value === this._props.payment)?.text || null
  }

  get amount(): string | null {
    return this._props.amount?.toLocaleString('ja-JP') || ''
  }

  get subtotal(): string | null {
    return this._props.subtotal?.toLocaleString('ja-JP') || ''
  }

  get commission(): string | null {
    return this._props.commission?.toLocaleString('ja-JP') || ''
  }

  get tax(): string | null {
    return this._props.tax?.toLocaleString('ja-JP') || ''
  }

  get total(): string | null {
    return ((this._props.subtotal || 0) + (this._props.tax || 0)).toLocaleString('ja-JP') || ''
  }

  get gmoOrderNo(): string | null {
    return this._props.gmoOrderNo
  }

  get gmoAccessNo(): string | null {
    return this._props.gmoAccessNo
  }

  get gmoAccessPass(): string | null {
    return this._props.gmoAccessPass
  }

  get gmoBranchNo(): number | null {
    return this._props.gmoBranchNo
  }

  get gmoCardNo(): number | null {
    return this._props.gmoCardNo
  }

  get gmoConfNo(): string | null {
    return this._props.gmoConfNo
  }

  get gmoReceiptNo(): string | null {
    return this._props.gmoReceiptNo
  }

  get gmoReceiptUrl(): string | null {
    return this._props.gmoReceiptUrl
  }

  get gmoTranNo(): string | null {
    return this._props.gmoTranNo
  }

  get status(): string {
    if (this._props.payment === 'np' || this._props.payment === 'direct') {
      return 'invoice'
    }

    return this._props.status || 'unpaid'
  }

  get unpaid(): boolean {
    return this.status === 'unpaid' || this.status === 'preparing' || this.status === 'processing' || this.status === 'error'
  }

  get gmoTranDate(): string | null {
    return this._props.gmoTranDate
  }

  get gmoConvenienceNo(): string | null {
    return this._props.gmoConvenienceNo
  }

  get confirmed(): boolean {
    return this._props.confirmed
  }

  get startedAt(): string | null {
    return this._props.startedAt
  }

  get endedAt(): string | null {
    return this._props.endedAt
  }

  get sentAt(): string | null {
    return this._props.sentAt
  }

  get createdAt(): string | null {
    return this._props.createdAt
  }

  get updatedAt(): string | null {
    return this._props.updatedAt
  }

  get properties(): OrderProperties | null | undefined {
    return this._props.properties
  }

  get user(): UserEntity | null | undefined {
    return this._props.user
  }

  get receipt(): string | null {
    return process.env.ASSETS_URL + `/${this._props.userId}/${this._props.title}.pdf`
  }

  get invoice(): boolean {
    return this._props.payment === 'np' || this._props.payment === 'direct'
  }
}

export const status = [
  { id: 'none', text: '支払い対象外', icon: '', color: '' },
  { id: 'paid', text: '支払い済', icon: 'icon-check-circle.svg', color: 'm-green' },
  { id: 'unpaid', text: '未払い', icon: 'icon-times-circle.svg', color: 'alert' },
  { id: 'processing', text: '支払い処理中', icon: 'icon-check-circle.svg', color: 'm-green' },
  { id: 'preparing', text: '支払い待ち', icon: 'icon-times-circle.svg', color: 'alert' },
  { id: 'completed', text: 'クレジット処理中', icon: 'icon-check-circle.svg', color: 'm-green' },
  { id: 'scheduled', text: '次回支払予定', icon: 'icon-undecided.svg', color: 'm-dark-gray' },
  { id: 'error', text: '支払い失敗', icon: 'icon-times-circle.svg', color: 'alert' },
  { id: 'invoice', text: '会社請求', icon: '', color: 'm-dark-gray' }
]

export const payments = [
  { text: '病院名', value: 'title' },
  { text: 'お支払い期間', value: 'term' },
  { text: '金額', value: 'amount' }
]

export const headers = [
  { text: 'ご利用内容', value: 'title', class: 'min' },
  { text: 'お支払い状況', value: 'status' },
  { text: 'お支払い方法', value: 'payment' },
  { text: '金額', value: 'amount' },
  { text: '', value: 'action' }
]

export const conveniences = [
  {
    id: 'seveneleven',
    code: '00007',
    name: 'セブンイレブン',
    img: 'https://static.mul-pay.jp/customer-convenience-store/img/conveni_nav_btn_seveneleven01.gif',
    url: 'https://static.mul-pay.jp/customer-convenience-store/seveneleven'
  },
  {
    id: 'lawson',
    code: '10001',
    name: 'ローソン',
    img: 'https://static.mul-pay.jp/customer-convenience-store/img/conveni_nav_btn_lowson01.gif',
    url: 'https://static.mul-pay.jp/customer-convenience-store/lawson/14.html'
  },
  {
    id: 'familymart',
    code: '10002',
    name: 'ファミリーマート',
    img: 'https://static.mul-pay.jp/customer-convenience-store/img/conveni_nav_btn_familymart01.gif',
    url: 'https://www.family.co.jp/services/application/receipt/shuunouhyou.html'
  },
  {
    id: 'ministop',
    code: '10005',
    name: 'ミニストップ',
    img: 'https://static.mul-pay.jp/customer-convenience-store/img/conveni_nav_btn_ministop01.gif',
    url: 'https://static.mul-pay.jp/customer-convenience-store/ministop/14.html'
  },
  {
    id: 'seicomart',
    code: '10008',
    name: 'セイコーマート',
    img: 'https://static.mul-pay.jp/customer-convenience-store/img/conveni_nav_btn_seicomart01.gif',
    url: 'https://static.mul-pay.jp/customer-convenience-store/seicomart/'
  }
]

export const EmptyOrderPropsFactory = (props?: Partial<IOrderProps> | null): IOrderProps => ({
  userId: null,
  title: null,
  payment: null,
  amount: null,
  status: null,
  subtotal: null,
  commission: null,
  tax: null,
  gmoOrderNo: null,
  gmoAccessNo: null,
  gmoAccessPass: null,
  gmoBranchNo: null,
  gmoCardNo: null,
  gmoConfNo: null,
  gmoReceiptNo: null,
  gmoReceiptUrl: null,
  gmoTranNo: null,
  gmoTranDate: null,
  gmoConvenienceNo: null,
  confirmed: false,
  startedAt: null,
  endedAt: null,
  sentAt: null,
  createdAt: null,
  updatedAt: null,
  properties: null,
  user: null,
  ...props
})
